import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutGrid, Martini, Mic, UtensilsCrossed, PartyPopper, Ticket, Music, Beer,
  Flame, Clock, ChevronRight, MapPin, LocateFixed, Bell, Check, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { getCity } from "@/lib/city";
import { SpotCard } from "@/components/SpotCard";
import { SectionHeader } from "@/components/SectionHeader";
import { FavoriteButton } from "@/components/FavoriteButton";
import { MapPreview } from "@/components/MapPreview";
import { formatDistance } from "@/lib/format";
import { useLanguage } from "@/i18n/LanguageContext";

const FILTERS = [
  { key: "tout", label: "Tout", icon: LayoutGrid },
  { key: "bars", label: "Bars", icon: Martini },
  { key: "karaoke", label: "Karaoké", icon: Mic },
  { key: "restaurants", label: "Restaurants", icon: UtensilsCrossed },
  { key: "soirees", label: "Soirées", icon: PartyPopper },
  { key: "activites", label: "Activités", icon: Ticket },
  { key: "concerts", label: "Concerts", icon: Music },
  { key: "happyhour", label: "Happy Hour", icon: Beer },
];

const SIDEBAR_CATS = [
  { key: "bars", label: "Bars", icon: Martini },
  { key: "karaoke", label: "Karaoké", icon: Mic },
  { key: "soirees", label: "Soirées", icon: PartyPopper },
  { key: "concerts", label: "Concerts", icon: Music },
  { key: "happyhour", label: "Happy Hour", icon: Beer },
  { key: "activites", label: "Activités", icon: Ticket },
];

const reveal = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.5, ease: "easeOut" },
};

const ScrollRow = ({ children, testId }) => {
  const ref = useRef(null);
  const { t } = useLanguage();
  return (
    <div className="relative">
      <div ref={ref} data-testid={testId} className="no-scrollbar flex snap-x gap-4 overflow-x-auto pb-1 lg:grid lg:grid-cols-4 lg:overflow-visible">
        {children}
      </div>
      <button
        type="button"
        data-testid={`${testId}-next`}
        aria-label={t("Défiler")}
        onClick={() => ref.current?.scrollBy({ left: 420, behavior: "smooth" })}
        className="absolute -right-3 top-1/3 hidden h-10 w-10 items-center justify-center rounded-full border border-slate-100 bg-white text-slate-600 shadow-lg transition hover:text-[#2563EB] lg:hidden"
      >
        <ChevronRight className="h-5 w-5" />
      </button>
    </div>
  );
};

const TonightCard = ({ event }) => {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  return (
    <article
      data-testid={`tonight-card-${event.slug}`}
      onClick={() => navigate(`/evenement/${event.slug}`)}
      className="group w-[240px] shrink-0 cursor-pointer snap-start overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-[0_4px_20px_rgba(15,23,42,0.06)] transition duration-300 hover:-translate-y-1 hover:border-blue-200 hover:shadow-[0_12px_32px_rgba(37,99,235,0.12)] lg:w-auto"
    >
      <div className="relative h-36 overflow-hidden">
        <img src={event.image} alt={event.title} loading="lazy" className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
        <span data-testid="tonight-time" className="absolute left-3 top-3 rounded-full bg-[#2563EB] px-2.5 py-1 text-[11px] font-bold text-white">
          {event.time_label}
        </span>
        <FavoriteButton item={event} itemType="evenement" className="absolute right-3 top-3" />
      </div>
      <div className="space-y-1 p-4">
        <h3 className="truncate text-sm font-semibold text-slate-900">{event.title}</h3>
        <p className="truncate text-sm text-slate-500">{event.venue}</p>
        <p className="flex items-center gap-1 text-xs text-slate-400">
          <MapPin className="h-3.5 w-3.5 text-[#2563EB]" />
          {formatDistance(event.distance_m, lang)}
        </p>
      </div>
    </article>
  );
};

const HappyHourCard = ({ spot }) => {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  return (
    <article
      data-testid={`happyhour-card-${spot.slug}`}
      onClick={() => navigate(`/etablissement/${spot.slug}`)}
      className="group flex min-w-[250px] shrink-0 cursor-pointer snap-start items-center gap-3 rounded-2xl border border-slate-100 bg-white p-3 shadow-[0_4px_16px_rgba(15,23,42,0.05)] transition duration-300 hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-[0_10px_24px_rgba(37,99,235,0.12)]"
    >
      <img src={spot.image} alt={spot.name} loading="lazy" className="h-20 w-20 shrink-0 rounded-xl object-cover" />
      <div className="min-w-0 flex-1 space-y-0.5">
        <h3 className="truncate text-sm font-semibold text-slate-900">{spot.name}</h3>
        <p className="text-xs text-slate-500">Happy Hour</p>
        <p className="flex items-center gap-1 text-xs font-medium text-slate-600">
          <Clock className="h-3.5 w-3.5 text-slate-400" />
          {spot.happy_hour}
        </p>
        <p className="flex items-center gap-1 text-xs text-slate-400">
          <MapPin className="h-3 w-3 text-[#2563EB]" />
          {formatDistance(spot.distance_m, lang)}
        </p>
      </div>
      <FavoriteButton item={spot} itemType="etablissement" inline />
    </article>
  );
};

export default function CeSoirPage() {
  const [spots, setSpots] = useState([]);
  const [tonightEvents, setTonightEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("tout");
  const [notif, setNotif] = useState(typeof Notification !== "undefined" ? Notification.permission : "denied");
  const navigate = useNavigate();
  const { t, lang } = useLanguage();

  useEffect(() => {
    Promise.all([api.get("/establishments"), api.get("/events", { params: { tonight: true } })])
      .then(([s, e]) => {
        setSpots(s.data);
        setTonightEvents(e.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const matchSpot = (s) => {
    if (filter === "tout") return true;
    if (filter === "happyhour") return Boolean(s.happy_hour);
    return s.category_key === filter;
  };
  const matchEvent = (e) => filter === "tout" || e.category_key === filter;

  const popular = useMemo(
    () =>
      spots
        .filter((s) => ["bars", "karaoke", "soirees", "concerts", "restaurants", "activites"].includes(s.category_key))
        .filter(matchSpot)
        .sort((a, b) => b.rating - a.rating)
        .slice(0, 8),
    [spots, filter]
  );
  const eventsTonight = useMemo(() => tonightEvents.filter(matchEvent), [tonightEvents, filter]);
  const happyHours = useMemo(() => spots.filter((s) => s.happy_hour).filter(matchSpot), [spots, filter]);

  const counts = useMemo(() => {
    const map = {};
    SIDEBAR_CATS.forEach((c) => {
      map[c.key] =
        c.key === "happyhour"
          ? spots.filter((s) => s.happy_hour).length
          : spots.filter((s) => s.category_key === c.key).length;
    });
    return map;
  }, [spots]);

  const mapMarkers = useMemo(
    () => [...popular.slice(0, 6), ...eventsTonight].map((m) => ({ ...m, color: undefined })),
    [popular, eventsTonight]
  );

  const cityName = getCity().split(",")[0];

  const geolocate = () => {
    if (!navigator.geolocation) return toast.error(t("La géolocalisation n'est pas disponible sur cet appareil"));
    navigator.geolocation.getCurrentPosition(
      () => toast.success(t("Position détectée — Québec, QC")),
      () => toast.error(t("Impossible de détecter ta position"))
    );
  };

  const enableNotifications = async () => {
    if (typeof Notification === "undefined") return toast.error(t("Les notifications ne sont pas supportées ici"));
    try {
      const p = await Notification.requestPermission();
      setNotif(p);
      if (p === "granted") toast.success(t("Notifications activées — à ce soir !"));
      else toast.error(t("Notifications refusées par le navigateur"));
    } catch {
      toast.error(t("Impossible d'activer les notifications ici"));
    }
  };

  return (
    <main data-testid="ce-soir-page" className="mx-auto max-w-[1800px] px-4 pb-16 pt-8 sm:px-6 sm:pt-10 lg:px-10">
      <Seo
        title={t("Ce soir à Québec — GoSpot")}
        siteName="GoSpot"
        description={t("Découvre les meilleures soirées, événements et happy hours à Québec ce soir.")}
      />

      <div className="grid gap-8 lg:grid-cols-[1fr_330px]">
        {/* COLONNE PRINCIPALE */}
        <div className="min-w-0">
          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: "easeOut" }}
            className="flex items-center gap-2 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl"
          >
            {lang === "en" ? `Tonight in ${cityName}` : `Ce soir à ${cityName}`}
            <Sparkles className="h-6 w-6 text-[#2563EB]" />
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.1 }}
            className="mt-2 text-base text-slate-600"
          >
            {t("Découvrez les meilleures soirées et événements près de vous.")}
          </motion.p>

          {/* FILTRES */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.18 }}
            className="no-scrollbar mt-6 flex snap-x gap-2.5 overflow-x-auto pb-1"
          >
            {FILTERS.map((f) => {
              const Icon = f.icon;
              const active = filter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  data-testid={`cesoir-filter-${f.key}`}
                  onClick={() => setFilter(f.key)}
                  className={`flex shrink-0 snap-start items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition duration-300 ${
                    active
                      ? "border-[#2563EB] bg-[#2563EB] text-white shadow-[0_6px_18px_rgba(37,99,235,0.25)]"
                      : "border-slate-200 bg-white text-slate-700 hover:-translate-y-0.5 hover:border-blue-200"
                  }`}
                >
                  <Icon className={`h-4 w-4 ${active ? "text-white" : "text-slate-500"}`} />
                  {t(f.label)}
                </button>
              );
            })}
          </motion.div>

          {/* SOIRÉES POPULAIRES */}
          {popular.length > 0 && (
            <motion.section {...reveal} className="mt-8" data-testid="soirees-populaires-section">
              <SectionHeader icon={Flame} iconColor="#F97316" title="Soirées populaires" linkTo="/explorer" testId="voir-tout-soirees" />
              <ScrollRow testId="soirees-row">
                {loading
                  ? [0, 1, 2, 3].map((i) => <div key={i} className="h-64 w-[240px] shrink-0 animate-pulse rounded-3xl bg-white" />)
                  : popular.map((spot) => (
                      <SpotCard key={spot.slug} spot={spot} className="w-[240px] shrink-0 snap-start lg:w-auto" />
                    ))}
              </ScrollRow>
            </motion.section>
          )}

          {/* ÉVÉNEMENTS CE SOIR */}
          {eventsTonight.length > 0 && (
            <motion.section {...reveal} className="mt-10" data-testid="evenements-ce-soir-section">
              <SectionHeader icon={Clock} iconColor="#2563EB" title="Événements ce soir" linkTo="/evenements" testId="voir-tout-evenements-soir" />
              <ScrollRow testId="tonight-row">
                {loading
                  ? [0, 1, 2, 3].map((i) => <div key={i} className="h-56 w-[240px] shrink-0 animate-pulse rounded-3xl bg-white" />)
                  : eventsTonight.map((event) => <TonightCard key={event.slug} event={event} />)}
              </ScrollRow>
            </motion.section>
          )}

          {/* HAPPY HOUR */}
          {happyHours.length > 0 && (
            <motion.section {...reveal} className="mt-10" data-testid="happy-hour-section">
              <SectionHeader icon={Beer} iconColor="#F59E0B" title="Happy Hour ce soir" linkTo="/explorer" testId="voir-tout-happyhour" />
              <div className="no-scrollbar flex snap-x gap-4 overflow-x-auto pb-1 sm:grid sm:grid-cols-2 sm:overflow-visible xl:grid-cols-4">
                {loading
                  ? [0, 1, 2, 3].map((i) => <div key={i} className="h-28 min-w-[250px] animate-pulse rounded-2xl bg-white" />)
                  : happyHours.map((spot) => <HappyHourCard key={spot.slug} spot={spot} />)}
              </div>
            </motion.section>
          )}

          {!loading && popular.length === 0 && eventsTonight.length === 0 && happyHours.length === 0 && (
            <div data-testid="cesoir-no-results" className="mt-10 rounded-3xl border border-slate-100 bg-white p-10 text-center">
              <p className="font-semibold text-slate-900">{t("Rien pour ce filtre ce soir")}</p>
              <p className="mt-1 text-sm text-slate-500">{t("Essaie une autre catégorie.")}</p>
              <button
                type="button"
                data-testid="cesoir-reset-button"
                onClick={() => setFilter("tout")}
                className="mt-4 rounded-full bg-[#2563EB] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#1D4ED8]"
              >
                {t("Voir tout")}
              </button>
            </div>
          )}
        </div>

        {/* COLONNE DROITE */}
        <aside className="space-y-5 self-start lg:sticky lg:top-24">
          <div data-testid="location-card" className="rounded-3xl border border-slate-100 bg-[#EDF3FE] p-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="flex items-center gap-1.5 text-xs text-slate-500">
                  <Sparkles className="h-3.5 w-3.5 text-[#2563EB]" />
                  {t("Basé sur votre position")}
                </p>
                <p className="mt-1.5 text-base font-bold text-slate-900">{getCity()}</p>
              </div>
              <button
                type="button"
                data-testid="cesoir-geolocate-button"
                onClick={geolocate}
                aria-label={t("Actualiser ma position")}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-blue-200 bg-white text-[#2563EB] transition hover:bg-blue-50"
              >
                <LocateFixed className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div data-testid="categories-card" className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
            <h2 className="text-base font-bold text-slate-900">{t("Catégories")}</h2>
            <div className="mt-3 space-y-1">
              {SIDEBAR_CATS.map((c) => {
                const Icon = c.icon;
                return (
                  <button
                    key={c.key}
                    type="button"
                    data-testid={`sidebar-cat-${c.key}`}
                    onClick={() => setFilter(c.key)}
                    className={`flex w-full items-center justify-between rounded-2xl px-3 py-2.5 text-sm transition ${
                      filter === c.key ? "bg-[#EFF6FF] font-semibold text-[#2563EB]" : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    <span className="flex items-center gap-2.5">
                      <Icon className={`h-4 w-4 ${filter === c.key ? "text-[#2563EB]" : "text-slate-400"}`} />
                      {t(c.label)}
                    </span>
                    <span className="text-xs font-medium text-slate-400">{counts[c.key] ?? 0}</span>
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              data-testid="all-categories-button"
              onClick={() => navigate("/explorer")}
              className="mt-4 w-full rounded-full border border-[#2563EB] px-4 py-2.5 text-sm font-semibold text-[#2563EB] transition hover:bg-[#EFF6FF]"
            >
              {t("Voir toutes les catégories")}
            </button>
          </div>

          <div data-testid="around-you-card" className="overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm">
            <div className="flex items-center justify-between px-5 pb-3 pt-4">
              <h2 className="text-base font-bold text-slate-900">{t("Autour de vous")}</h2>
              <button
                type="button"
                data-testid="around-you-map-link"
                onClick={() => navigate("/explorer?vue=carte")}
                className="text-sm font-semibold text-[#2563EB] transition hover:underline"
              >
                {t("Voir la carte")}
              </button>
            </div>
            <div className="px-3 pb-3">
              <MapPreview markers={mapMarkers} showButton={false} />
            </div>
          </div>

          <div data-testid="notifications-card" className="rounded-3xl border border-blue-100 bg-[#EDF3FE] p-5">
            <h2 className="flex items-center gap-2 text-base font-bold text-slate-900">
              <Bell className="h-5 w-5 text-[#2563EB]" />
              {t("Ne manquez rien ce soir !")}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              {t("Activez les notifications et recevez les meilleures suggestions.")}
            </p>
            <button
              type="button"
              data-testid="enable-notifications-button"
              onClick={enableNotifications}
              disabled={notif === "granted"}
              className={`mt-4 flex w-full items-center justify-center gap-2 rounded-full px-4 py-3 text-sm font-semibold transition ${
                notif === "granted"
                  ? "cursor-default bg-[#2563EB] text-white"
                  : "border border-[#2563EB] bg-white text-[#2563EB] hover:bg-[#EFF6FF]"
              }`}
            >
              {notif === "granted" && <Check className="h-4 w-4" />}
              {notif === "granted" ? t("Notifications activées") : t("Activer les notifications")}
            </button>
          </div>
        </aside>
      </div>
    </main>
  );
}
